import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { alpha } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import HttpMethodChip, { httpMethodsToBackgroundColor } from "@common/components/ApiConfiguration/HttpMethodChip";
import OrganizationRole from "../../../models/enums/organization-roles";
import {
  endpointDescriptionKey,
  endpointTitleKey,
  scopeLabelKey,
  ExternalApiEndpoint,
} from "../../../models/organization/openapi";
import TooltipIconButton from "@common/components/Inputs/TooltipIconButton";
import { CopyIcon } from "@common/utils/icons/icons";

interface ExternalApiEndpointCardProps {
  endpoint: ExternalApiEndpoint;
  isCallable: boolean;
}

const ExternalApiEndpointCard: React.FC<ExternalApiEndpointCardProps> = ({ endpoint, isCallable }) => {
  const { t } = useTranslation();

  const [copied, setCopied] = useState(false);

  const accent = httpMethodsToBackgroundColor.get(endpoint.method) ?? grey[500];
  const title = t(endpointTitleKey(endpoint), { defaultValue: endpoint.summary });
  const description = t(endpointDescriptionKey(endpoint), { defaultValue: "" });
  const roleLabel =
    endpoint.requiredRole === OrganizationRole.MANUFACTURER
      ? t("label-api-endpoint-manufacturer-only")
      : endpoint.requiredRole;

  const copyUrlClickHandler = async () => {
    try {
        if (!navigator.clipboard) return;

        await navigator.clipboard.writeText(endpoint.url);
        setCopied(true);
    } catch {
        setCopied(false);
    }
  };

  return (
    <Box
      data-testid={`api-endpoint-${endpoint.id}`}
      sx={(theme) => ({
        border: `1px solid ${accent}`,
        background: alpha(accent, 0.12),
        borderRadius: 1,
        padding: `${theme.spacing(2)} !important`,
        display: "flex",
        flexFlow: "column nowrap",
        gap: 1,
      })}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <HttpMethodChip httpMethod={endpoint.method} />
          <Typography fontWeight="bold" color={grey[900]} sx={{ wordBreak: "break-all" }}>
            {title}
          </Typography>
          <Typography color={grey[800]}>{endpoint.url}</Typography>
          <TooltipIconButton tooltip={t("copy-to-clipboard")} icon={<CopyIcon />} onClick={copyUrlClickHandler} />
          {copied && (
            <Typography sx={(theme) => ({ color: theme.palette.success.main })}>{t("label-copied")}</Typography>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {!isCallable && (
            <Tooltip title={t("info-api-endpoint-scope-missing")}>
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                icon={<WarningAmberIcon />}
                label={t("label-api-endpoint-no-key-with-scope")}
                data-testid={`api-endpoint-scope-missing-${endpoint.id}`}
              />
            </Tooltip>
          )}
          {roleLabel != null && <Chip size="small" color="warning" label={roleLabel} />}
          {endpoint.requiredScope != null && (
            <Tooltip title={t("label-api-endpoint-required-scope")}>
              <Chip
                size="small"
                label={t(scopeLabelKey(endpoint.requiredScope))}
                data-testid={`api-endpoint-scope-${endpoint.id}`}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {description.length > 0 && (
        <Typography variant="body2" color={grey[800]}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default ExternalApiEndpointCard;
